import React, { Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate
} from 'react-router-dom';

import { AuthProvider, useAuth } from './context/AuthContext';
import { ResumeProvider } from './context/ResumeContext';
import { ThemeProvider } from './context/ThemeContext';
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import ScrollToTop from './components/ScrollToTop';
import Spinner from './components/Spinner';
import ThemeToggle from './components/ThemeToggle';

import './styles/globals.css';

const PublicRoutes = React.lazy(() => import('./routes/PublicRoutes'));
const EmployerRoutes = React.lazy(() => import('./routes/EmployerRoutes'));
const AdminRoutes = React.lazy(() => import('./routes/AdminRoutes'));

const ConditionalHeader = () => {
  const location = useLocation();
  const { user } = useAuth();

  // Hide header for auth pages, employer onboarding, and all employer dashboard/action pages
const isEmployerHome = location.pathname === '/employer' || location.pathname === '/employer/home';

  const hideHeader = location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/verify-email') ||
    (location.pathname.startsWith('/employer') && !isEmployerHome);

  return hideHeader ? null : <Header />;
};


const ConditionalFooter = () => {
  const location = useLocation();
  const { user } = useAuth();

  // Hide footer for auth pages, employer pages, admin pages, and when employer is logged in
  const hideFooter = location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/register') ||
    location.pathname.startsWith('/verify-email') ||
    location.pathname.startsWith('/employer') ||
    location.pathname.startsWith('/admin') ||
    (user && user.role === 'employer');

  return hideFooter ? null : <Footer />;
};

// Dashboard and authentication routes do not render the public header, so keep
// the same global preference reachable there without duplicating it elsewhere.
const FloatingThemeToggle = () => {
  const location = useLocation();
  const hasHeader = !location.pathname.startsWith('/login') &&
    !location.pathname.startsWith('/register') &&
    !location.pathname.startsWith('/verify-email') &&
    (location.pathname === '/employer' || location.pathname === '/employer/home' || !location.pathname.startsWith('/employer')) &&
    !location.pathname.startsWith('/admin');

  return hasHeader ? null : (
    <div className="fixed right-4 top-4 z-[60]">
      <ThemeToggle className="shadow-lg" />
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ResumeProvider>
        <Router>
          <ScrollToTop />
          <FloatingThemeToggle />

          <div className="cf-page">
            <ConditionalHeader />

            <main className="min-h-screen">
              <Suspense fallback={<Spinner />}>
                <Routes>
                  {/* Employer Routes */}
                  <Route path="/employer/*" element={<EmployerRoutes />} />

                  {/* Admin Routes */}
                  <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="/admin/*" element={<AdminRoutes />} />

                  {/* Fallback / Public Routes */}
                  <Route path="*" element={<RequireRoleRedirect />} />
                </Routes>
              </Suspense>
            </main>

            <ConditionalFooter />
          </div>
        </Router>
        </ResumeProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

const RequireRoleRedirect = () => {
  const location = useLocation();
  const { user, loading } = useAuth();

  if (loading) return <Spinner />;

  // If admin: keep them inside /admin/* only
  if (user?.role === 'admin' && !location.pathname.startsWith('/admin')) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // If non-admin: route to existing app areas
  if (location.pathname.startsWith('/employer')) {
    return <EmployerRoutes />;
  }

  // Everything else goes to public routes (including /dashboard)
  return <PublicRoutes />;
};

export default App;
